import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { get } from 'lodash';

import Select from 'components/Form/Select';
import Checkbox from 'components/Form/Checkbox';
import { formatCurrency } from 'utils/formatCurrency';
import { floatUnmask } from 'utils/unmasks';
import { EMPLOYMENT_YEARS } from 'utils/appConstants';
import imgError from 'assets/images/error.svg';
import { consentLabel } from 'containers/review/constant';
import {
  newNextAction,
  NEW_NEXT,
} from '../../actions/navigation';
import Modal from '../../components/Modal';
import { useConfig } from '../../config/ConfigContext';
import Header from '../../components/Header';
import ContentWrapper from '../../components/ContentWrapper';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Footer from '../../components/Footer';
import Pagination from '../../components/Pagination';
import Input from '../../components/Form/Input';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Transition from '../../components/Transition';
import Container from '../../components/Container';
import Progress from '../../components/Progress';
import Headline from '../../components/Headline';
import ApplicationId from '../../components/ApplicationId';

import schema from './loanDetailsSchema';

import './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
});

const unmask = (val) => val.replace(/[$, ]+/g, '');

const LoanDetails = (props) => {
  const config = useConfig();
  const { checkinData, navigate, validator: { values, errors }, params, searchParams } = props;
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popoverContent, setPopoverContent] = useState('');
  const [isContentPopupVisible, setIsContentPopupVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(null);
  const employmentStatus = useMemo(() => get(values, 'employmentStatus'), [values]);
  const isEmploymentInfoRequired = useMemo(() => !(employmentStatus === 'retired' || employmentStatus === 'unemployed' || employmentStatus === 'student'), [employmentStatus]);
  const workflowType = get(params, 'workflowType') || 'text2apply';
  const key = searchParams.get('key');
  const test = searchParams.get('test');

  useEffect(() => {
    if (!isEmploymentInfoRequired) {
      const formData = {
        ...values,
        employmentYears: '',
      };
      props.validator.setValues(formData);
    }
  }, [isEmploymentInfoRequired]);

  useEffect(() => {
    const { validator: { setValues }, testSubjectData } = props;

    let setTestSubjectValues = {};
    if (testSubjectData && testSubjectData.loanDetails && testSubjectData.loanDetails.value) {
      const { value } = testSubjectData.loanDetails;
      setTestSubjectValues = {
        annualGrossIncome: value.yourAnnualGrossIncome || '',
        employmentStatus: value.employmentStatus || '',
        employmentYears: value.yearsWithCurrentEmployer || '',
        consentToVerifyIncomeEmployment: true,
      };
    }

    if (get(checkinData, ['data', 'data']) === undefined) {
      if (test) {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}&test=${test}`);
      } else {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
      }
    } else {
      const initialFormData = {
        loanAmount: `${checkinData.data.data.loanAmount}`,
        ...setTestSubjectValues,
      };
      setValues(initialFormData);
    }
  }, []);

  useEffect(() => {
    const { fetch } = props;

    if (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`]) {
      const routeUrl = checkinData.data && checkinData.data.url.replace(config.hostUrl, '');
      navigate(routeUrl);
    }
  }, [checkinData]);

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleCheckboxChange = (name, value) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(name, value);
  };

  const handleBlur = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value ? formatCurrency(floatUnmask(event.target.value), 2) : '');
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const { dispatch, validator: { validate } } = props;

    if (validate(schema).isValid) {
      const formData = test
        ? {
          loanAmount: unmask(values.loanAmount),
          grossAnnualIncome: unmask(values.annualGrossIncome || 0),
          employmentStatus: values.employmentStatus,
          employmentYears: values.employmentYears,
          test,
        }
        : {
          loanAmount: unmask(values.loanAmount),
          grossAnnualIncome: unmask(values.annualGrossIncome || 0),
          employmentStatus: values.employmentStatus,
          employmentYears: values.employmentYears,
        };
      setIsProgressVisible(true);

      dispatch(newNextAction({
        key,
        workflowType,
        data: formData,
        onUploadProgress: (progressEvent) => {
          setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        fail: (err) => {
          console.log(err);
          setIsPopupVisible(true);
          setIsProgressVisible(false);
        },
      }));
    }
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const togglePopover = (content) => {
    setPopoverContent(content);
    setIsContentPopupVisible(!isContentPopupVisible);
  };

  const combinedLabelText = (
    <p>
      <div onClick={() => togglePopover(consentLabel[0].content)} className="text-primary consentLabel">{consentLabel[0].title}</div>
      {' '}
      and
      {' '}
      <div onClick={() => togglePopover(consentLabel[1].content)} className="text-primary consentLabel">{consentLabel[1].title}</div>
    </p>
  );

  return (
    <Transition className="transition-item wrapper">
      <Header />
      <ContentWrapper>
        <ApplicationId id={get(checkinData, 'attributes.entityId')} inHeadline />
        <Headline
          appIdVisible
          header={(
            <>
              Verify your
              {' '}
              <span>loan details</span>
              {' '}
              and provide some
              {' '}
              <span>financial info</span>
              .
            </>
          )}
          title={(
            <span className="boldText">
              All stated income and/or employment details will require verification prior to final loan approval. Please note that this  process may result in changes to the terms and conditions of your loan.
            </span>
          )}
        />
        <Container className="container">
          <FormRow>
            <FormColumn>
              <Input
                label="Loan Amount"
                name="loanAmount"
                tooltip="If total loan amount is incorrect, please contact your provider to have this modified."
                isMasked={currencyMask}
                value={values.loanAmount ? `${formatCurrency(values.loanAmount || 0, 2)}` : ''}
                onChange={handleInputChange}
                readOnly
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Your Annual Gross Income"
                name="annualGrossIncome"
                tooltip={<>Your total verifiable gross income might include your salary, retirement income or other sources of income. You don&apos;t have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.</>}
                type="tel"
                isMasked={currencyMask}
                value={values.annualGrossIncome ? `${values.annualGrossIncome}` : ''}
                onChange={handleInputChange}
                hasError={errors && errors.annualGrossIncome}
                onBlur={handleBlur}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Employment Status"
                name="employmentStatus"
                data={[
                  { value: 'employed', title: 'Employed' },
                  { value: 'self-employed', title: 'Self Employed' },
                  { value: 'unemployed', title: 'Unemployed' },
                  { value: 'student', title: 'Student' },
                  { value: 'retired', title: 'Retired' },
                  { value: 'military', title: 'Military' },
                ]}
                value={values.employmentStatus || ''}
                onChange={handleInputChange}
                hasError={errors && errors.employmentStatus}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Years with Current Employer"
                name="employmentYears"
                data={Object.keys(EMPLOYMENT_YEARS).map((k) => ({ title: EMPLOYMENT_YEARS[k], value: k }))}
                value={values.employmentYears || ''}
                onChange={handleInputChange}
                hasError={errors && errors.employmentYears}
                isRequired={isEmploymentInfoRequired}
                isDisabled={!isEmploymentInfoRequired}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <h3>By checking the box below I agree to the following: </h3>
              <div className="checkbox-wrapper">
                <Checkbox
                  name="consentToVerifyIncomeEmployment"
                  onChange={handleCheckboxChange.bind(null, 'consentToVerifyIncomeEmployment', !values.consentToVerifyIncomeEmployment)}
                  isChecked={values.consentToVerifyIncomeEmployment}
                />
                <div>
                  {combinedLabelText}
                </div>
              </div>
              <span className="error">{errors && errors.consentToVerifyIncomeEmployment}</span>
              {
                    isContentPopupVisible
                    && (
                      <Modal>
                        <div className="card-header">
                          <h5>Terms & Conditions</h5>
                        </div>
                        <div className="close" onClick={() => setIsContentPopupVisible(false)} />
                        <div className="card-content popoverContent">
                          {popoverContent}
                        </div>
                      </Modal>
                    )
                  }
            </FormColumn>
          </FormRow>
        </Container>
      </ContentWrapper>
      <Footer>
        <FormRow>
          <FormColumn>
            <Button
              className="btn btn-primary arrow"
              onClick={handleSubmitFrom}
              title="Next: Identity Verification"
            />
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn>
            <Pagination activeIndex={1} />
          </FormColumn>
        </FormRow>
      </Footer>
      {
        isPopupVisible
          && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>Please fix the errors.</p>
              <div onClick={handleClosePopup} className="btn btn-red">OK</div>
            </div>
          </Modal>
          )
      }
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

LoanDetails.propTypes = {
  navigate: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  checkinData: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  testSubjectData: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
  testSubjectData: state.screen.testSubjectData,
});

export default Validator(schema)(connect(mapStateToProps)(LoanDetails));
